import { ElementRef, inject, Injectable } from '@angular/core';
import { assert } from '@sama/js-core';
import { InjectableSuperclass } from '@sama/ng-core';
import {
  bottomToLeftRotationCursor,
  leftToTopRotationCursor,
  rightToBottomRotationCursor,
  topToRightRotationCursor,
} from '../../../cross-team-shared/cursors/custom-cursors';
import { MouseEventService } from '../../../cross-team-shared/misc-services/events/mouse-event.service';
import { setCssProperty } from '../../../cross-team-shared/misc-utils';
import { assertDefined } from '../../../cross-team-shared/misc-utils/strict-record';
import { Point2D } from '../../../team-apis/otterdam/answers/shape-typings';
import { EditPanelCamera } from '../../render/cameras/edit-panel-camera';
import { SideViewCamerasService } from '../../render/cameras/side-view-cameras.service';
import { PointCloudContentStore } from '../../state/point-cloud-content-store';
import {
  getContainerPoint,
  getCuboidNdcBoundingBox,
  isPointInsideBox,
} from './drag-handlers/drag-handler-utils';
import {
  getEdgeTolerance,
  isPointWithinBottomEdge,
  isPointWithinBottomLeftCorner,
  isPointWithinBottomRightCorner,
  isPointWithinLeftEdge,
  isPointWithinRightEdge,
  isPointWithinTopEdge,
  isPointWithinTopLeftCorner,
  isPointWithinTopRightCorner,
} from './drag-handlers/resize-utils';
import {
  getBottomLeftHandle,
  getBottomRightHandle,
  getTopLeftHandle,
  getTopRightHandle,
} from './drag-handlers/rotation-drag-handler';
import { Axis } from './edit-panel.component';

@Injectable()
export class EditPanelCursorService extends InjectableSuperclass {
  private readonly store = inject(PointCloudContentStore);
  private readonly sideViewCamerasService = inject(SideViewCamerasService);
  private readonly mouseEventService = inject(MouseEventService);

  private camera?: EditPanelCamera;
  private axis?: Axis;
  private containerElement?: ElementRef<HTMLElement>;

  constructor() {
    super();

    this.subscribeTo(
      this.mouseEventService.getEvent$('container', 'mousemove'),
      ({ event }) => {
        if (this.store.state().render.currentEditSource) {
          return;
        }

        assertDefined(this.containerElement);

        const cursor = this.getCursorString(event);
        setCssProperty(this.containerElement, '--h3-panel-cursor', cursor);
      },
    );
  }

  install(element: ElementRef<HTMLElement>, axis: Axis) {
    this.containerElement = element;
    this.axis = axis;
    const camera = this.sideViewCamerasService.getCameraByAxis(axis);
    assert(
      camera instanceof EditPanelCamera,
      'camera must be an EditPanelCamera when using "new-edit-panel" feature flag',
    );
    this.camera = camera;
  }

  private getCursorString(event: MouseEvent) {
    assertDefined(this.camera);

    const containerPoint = getContainerPoint(event);
    const focusedCuboidBoundingBox = getCuboidNdcBoundingBox(
      this.store.getFocusedShapeLocation(),
      this.camera,
    );

    assertDefined(this.axis);
    const viewRegion = this.store.getViewRegionStore(this.axis).state();
    const tolerance = getEdgeTolerance(viewRegion, this.camera);
    return (
      getCornerCursor(containerPoint, focusedCuboidBoundingBox, tolerance) ||
      getEdgeCursor(containerPoint, focusedCuboidBoundingBox, tolerance) ||
      getTranslateCursor(containerPoint, focusedCuboidBoundingBox) ||
      getRotationCursor(containerPoint, focusedCuboidBoundingBox) ||
      undefined
    );
  }
}

function getEdgeCursor(
  containerPoint: Point2D,
  boundingBox: { min: Point2D; max: Point2D },
  tolerance: number,
) {
  if (
    isPointWithinLeftEdge(containerPoint, boundingBox, tolerance) ||
    isPointWithinRightEdge(containerPoint, boundingBox, tolerance)
  ) {
    return 'ew-resize';
  } else if (
    isPointWithinTopEdge(containerPoint, boundingBox, tolerance) ||
    isPointWithinBottomEdge(containerPoint, boundingBox, tolerance)
  ) {
    return 'ns-resize';
  }

  return undefined;
}

function getCornerCursor(
  containerPoint: Point2D,
  boundingBox: { min: Point2D; max: Point2D },
  tolerance: number,
) {
  if (
    isPointWithinBottomLeftCorner(containerPoint, boundingBox, tolerance) ||
    isPointWithinTopRightCorner(containerPoint, boundingBox, tolerance)
  ) {
    return 'nesw-resize';
  } else if (
    isPointWithinBottomRightCorner(containerPoint, boundingBox, tolerance) ||
    isPointWithinTopLeftCorner(containerPoint, boundingBox, tolerance)
  ) {
    return 'nwse-resize';
  }

  return undefined;
}

function getTranslateCursor(
  containerPoint: Point2D,
  boundingBox: { min: Point2D; max: Point2D },
) {
  return isPointInsideBox(containerPoint, boundingBox) ? 'move' : undefined;
}

function getRotationCursor(
  containerPoint: Point2D,
  boundingBox: { min: Point2D; max: Point2D },
) {
  if (isPointInsideBox(containerPoint, getTopLeftHandle(boundingBox))) {
    return leftToTopRotationCursor;
  } else if (isPointInsideBox(containerPoint, getTopRightHandle(boundingBox))) {
    return topToRightRotationCursor;
  } else if (
    isPointInsideBox(containerPoint, getBottomRightHandle(boundingBox))
  ) {
    return rightToBottomRotationCursor;
  } else if (
    isPointInsideBox(containerPoint, getBottomLeftHandle(boundingBox))
  ) {
    return bottomToLeftRotationCursor;
  }
  return undefined;
}
